/**
 * EconomicCalendarConfigModal Component (Trading Hub)
 *
 * Quick configuration modal for Economic Calendar settings accessible from
 * the Pre-Trading workflow gear icon.
 *
 * Features:
 * - Country filter multi-select with search, Select All, Clear All
 * - Flag emoji + full country name display (86 countries)
 * - Impact level multi-select (Low/Medium/High checkboxes)
 * - "Show selected countries only" toggle with auto-untoggle on search
 * - "More settings" link to full settings page
 * - Fresh data fetch on modal open
 * - Cross-tab sync on save
 * - Dirty state protection (blocks X/backdrop/Esc when dirty)
 *
 * Note: This is the Trading Hub version. A separate modal exists in Settings
 * at src/components/Settings/TradingWorkflow/PreTrading/EconomicCalendarConfigModal.tsx
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardHeader,
  CardBody,
  CheckboxGroup,
  Checkbox,
  ScrollShadow,
  Input,
  useToastNotification,
} from '@enfomentis/ui';
import { economicCalendarSettingsService } from '@/services/EconomicCalendarSettingsService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { COUNTRY_NAMES, COUNTRY_TO_CURRENCY } from '@/constants/economicCalendar';
import { getCountryFlagEmoji } from '@/utils/economicCalendar';
import type { EconomicCalendarSettings } from '@/types/schema/EconomicCalendarSettings';

// ============================================================================
// Types
// ============================================================================

/** Valid impact level values */
type ImpactLevel = 'low' | 'medium' | 'high';

export interface EconomicCalendarConfigModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Callback on successful save */
  onSaveSuccess?: (updatedSettings: EconomicCalendarSettings) => void;
}

interface CountryOption {
  value: string;
  label: string;
  searchText: string;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Build country options array from COUNTRY_TO_CURRENCY mapping
 * Format: "🇺🇸 United States (USD)" - flag emoji + full name + currency
 * Explicitly sorted A-Z by country code for deterministic order
 */
const COUNTRY_OPTIONS: CountryOption[] = Object.entries(COUNTRY_TO_CURRENCY)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([countryCode, currency]) => {
    const countryName = COUNTRY_NAMES[countryCode as keyof typeof COUNTRY_NAMES] ?? countryCode;
    const flag = getCountryFlagEmoji(countryCode);
    return {
      value: countryCode,
      label: `${flag} ${countryName} (${currency})`,
      // Search text includes code, name, and currency for comprehensive matching
      searchText: `${countryCode} ${countryName} ${currency}`.toLowerCase(),
    };
  });

/**
 * Default impact levels (all selected) - canonical order: low → medium → high
 */
const DEFAULT_IMPACT_LEVELS: readonly ImpactLevel[] = ['low', 'medium', 'high'];

/**
 * Impact level options for checkbox display
 */
const IMPACT_LEVEL_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
] as const;

/**
 * Normalizes impact levels array: filters to valid values and orders by canonical order.
 * Used for consistent comparison in dirty tracking, baseline assignment, and save.
 */
function normalizeImpactLevels(values: string[]): ImpactLevel[] {
  const validSet = new Set<string>(DEFAULT_IMPACT_LEVELS);
  return DEFAULT_IMPACT_LEVELS.filter((level) => values.includes(level) && validSet.has(level));
}

// ============================================================================
// Component
// ============================================================================

export const EconomicCalendarConfigModal: React.FC<
  EconomicCalendarConfigModalProps
> = ({ isOpen, onClose, onSaveSuccess }) => {
  const navigate = useNavigate();
  const { showToast } = useToastNotification();

  // Local editing state
  const [localCountryFilters, setLocalCountryFilters] = useState<string[]>([]);
  const [localImpactLevels, setLocalImpactLevels] = useState<ImpactLevel[]>([
    ...DEFAULT_IMPACT_LEVELS,
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settingsId, setSettingsId] = useState<string | null>(null);

  // Search state for country filter
  const [searchQuery, setSearchQuery] = useState('');

  // Show selected countries only toggle
  const [showSelectedCountriesOnly, setShowSelectedCountriesOnly] = useState(false);

  // Baseline ref for dirty tracking
  const baselineRef = useRef<{
    countryFilters: string[];
    impactLevels: ImpactLevel[];
  } | null>(null);

  // Track previous isOpen to detect open transition
  const wasOpenRef = useRef(false);

  // Filter country options based on search query
  const filteredCountryOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return COUNTRY_OPTIONS;
    }
    const query = searchQuery.toLowerCase().trim();
    return COUNTRY_OPTIONS.filter((option) => option.searchText.includes(query));
  }, [searchQuery]);

  // Further filter for "show selected countries only" mode
  const filteredCountryOptionsDisplay = useMemo(() => {
    if (!showSelectedCountriesOnly) {
      return filteredCountryOptions;
    }
    const selectedSet = new Set(localCountryFilters);
    return filteredCountryOptions.filter((option) => selectedSet.has(option.value));
  }, [filteredCountryOptions, showSelectedCountriesOnly, localCountryFilters]);

  // Compute dirty state
  const isDirty = useMemo(() => {
    if (!baselineRef.current) return false;
    const countryFiltersChanged =
      JSON.stringify([...localCountryFilters].sort()) !==
      JSON.stringify([...baselineRef.current.countryFilters].sort());
    const impactLevelsChanged =
      JSON.stringify(normalizeImpactLevels(localImpactLevels)) !==
      JSON.stringify(normalizeImpactLevels(baselineRef.current.impactLevels));
    return countryFiltersChanged || impactLevelsChanged;
  }, [localCountryFilters, localImpactLevels]);

  // ============================================================================
  // Initialization
  // ============================================================================

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal just opened - load fresh settings and reset UI state
      const loadSettings = async () => {
        setIsLoading(true);
        setSearchQuery(''); // Reset search on open
        setShowSelectedCountriesOnly(false); // Reset toggle on open
        try {
          const settings = await economicCalendarSettingsService.getCurrentSettings();

          if (settings) {
            setSettingsId(settings.id);
            setLocalCountryFilters(settings.countryFilters ?? []);
            const normalizedLevels = normalizeImpactLevels(
              settings.impactLevels ?? [...DEFAULT_IMPACT_LEVELS]
            );
            setLocalImpactLevels(normalizedLevels);

            // Set baseline for dirty tracking
            baselineRef.current = {
              countryFilters: settings.countryFilters ?? [],
              impactLevels: normalizedLevels,
            };
          } else {
            // No settings found - use defaults
            setSettingsId(null);
            setLocalCountryFilters([]);
            setLocalImpactLevels([...DEFAULT_IMPACT_LEVELS]);
            baselineRef.current = {
              countryFilters: [],
              impactLevels: [...DEFAULT_IMPACT_LEVELS],
            };
          }
        } catch (err) {
          console.error('Failed to load economic calendar settings:', err);
          showToast({
            title: 'Load failed',
            description: 'Could not load settings. Please try again.',
            color: 'danger',
          });
          setSettingsId(null);
        } finally {
          setIsLoading(false);
        }
      };
      loadSettings();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, showToast]);

  /**
   * Auto-untoggle showSelectedCountriesOnly when search becomes non-empty
   * Does NOT auto-re-enable when search is cleared (user must manually re-enable)
   */
  useEffect(() => {
    if (searchQuery.trim().length > 0 && showSelectedCountriesOnly) {
      setShowSelectedCountriesOnly(false);
    }
  }, [searchQuery, showSelectedCountriesOnly]);

  /**
   * Auto-reset showSelectedCountriesOnly when country selection becomes empty
   * Prevents user being trapped in empty "show selected" view
   */
  useEffect(() => {
    if (localCountryFilters.length === 0 && showSelectedCountriesOnly) {
      setShowSelectedCountriesOnly(false);
    }
  }, [localCountryFilters.length, showSelectedCountriesOnly]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleSave = useCallback(async () => {
    if (!settingsId) {
      showToast({
        title: 'Save failed',
        description: 'Settings not loaded. Please close and try again.',
        color: 'danger',
      });
      return;
    }

    setIsSaving(true);

    try {
      const updatedSettings = await economicCalendarSettingsService.update(
        settingsId,
        {
          countryFilters: localCountryFilters,
          impactLevels: localImpactLevels,
        }
      );

      // Update baseline
      baselineRef.current = {
        countryFilters: localCountryFilters,
        impactLevels: normalizeImpactLevels(localImpactLevels),
      };

      // Notify other tabs
      syncCoordinatorService.notifyChange('economic_calendar_settings', settingsId);

      showToast({
        title: 'Settings saved',
        description: 'Economic calendar filters updated.',
        color: 'success',
      });

      onSaveSuccess?.(updatedSettings);
      onClose();
    } catch (err) {
      console.error('Failed to save economic calendar settings:', err);
      showToast({
        title: 'Save failed',
        description: 'An error occurred while saving. Please try again.',
        color: 'danger',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    settingsId,
    localCountryFilters,
    localImpactLevels,
    showToast,
    onSaveSuccess,
    onClose,
  ]);

  const handleDiscard = useCallback(() => {
    // Reset to baseline
    if (baselineRef.current) {
      setLocalCountryFilters(baselineRef.current.countryFilters);
      setLocalImpactLevels(baselineRef.current.impactLevels);
    }
    setSearchQuery('');
    setShowSelectedCountriesOnly(false);
    onClose();
  }, [onClose]);

  const handleNavigateToSettings = useCallback(() => {
    onClose();
    navigate('/settings/workflow/economic-calendar');
  }, [onClose, navigate]);

  const handleCountryFiltersChange = useCallback((values: string[]) => {
    setLocalCountryFilters(values);
  }, []);

  const handleImpactLevelsChange = useCallback((values: string[]) => {
    setLocalImpactLevels(normalizeImpactLevels(values));
  }, []);

  const handleToggleShowSelectedCountries = useCallback(() => {
    // If toggling ON and search has text, clear the search
    if (!showSelectedCountriesOnly && searchQuery.trim().length > 0) {
      setSearchQuery('');
    }
    setShowSelectedCountriesOnly((prev) => !prev);
  }, [showSelectedCountriesOnly, searchQuery]);

  /**
   * Select All: Union visible (filtered) items with current selection
   */
  const handleSelectAll = useCallback(() => {
    const visibleValues = new Set(filteredCountryOptionsDisplay.map((o) => o.value));
    setLocalCountryFilters((prev) => {
      const combined = new Set([...prev, ...visibleValues]);
      return Array.from(combined);
    });
  }, [filteredCountryOptionsDisplay]);

  /**
   * Clear All: Remove only visible (filtered) items from selection
   */
  const handleClearAll = useCallback(() => {
    const visibleValues = new Set(filteredCountryOptionsDisplay.map((o) => o.value));
    setLocalCountryFilters((prev) => prev.filter((v) => !visibleValues.has(v)));
  }, [filteredCountryOptionsDisplay]);

  // ============================================================================
  // Render
  // ============================================================================

  // Disable save when: not dirty, saving, loading, or no settings loaded
  const isSaveDisabled = !isDirty || isSaving || isLoading || !settingsId;

  // Count how many of the visible options are selected
  const visibleSelectedCount = filteredCountryOptionsDisplay.filter((o) =>
    localCountryFilters.includes(o.value)
  ).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="top-center"
      scrollBehavior="inside"
      size="lg"
      isDismissable={!isDirty}
      hideCloseButton={isDirty}
      isKeyboardDismissDisabled={isDirty}
    >
      <ModalContent>
        <ModalHeader>
          <span className="text-lg font-semibold">Economic Calendar Settings</span>
        </ModalHeader>
        <ModalBody className="gap-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <span className="text-default-500">Loading...</span>
            </div>
          ) : (
            <>
              {/* Impact Level Selection */}
              <Card shadow="sm" className="shrink-0">
                <CardHeader className="flex flex-col items-start gap-1 pb-0">
                  <p className="text-sm font-medium">Impact Filter</p>
                  <p className="text-sm text-default-500">
                    Selected impact levels will show by default
                  </p>
                  <p className="text-sm text-default-300">
                    None selected = no events shown
                  </p>
                </CardHeader>
                <CardBody className="gap-2">
                  <CheckboxGroup
                    orientation="horizontal"
                    value={localImpactLevels}
                    onValueChange={handleImpactLevelsChange}
                  >
                    {IMPACT_LEVEL_OPTIONS.map((option) => (
                      <Checkbox key={option.value} value={option.value}>
                        {option.label}
                      </Checkbox>
                    ))}
                  </CheckboxGroup>
                </CardBody>
              </Card>

              {/* Country Filter Selection */}
              <Card shadow="sm">
                <CardHeader className="flex flex-col items-start gap-1 pb-0">
                  <div className="flex items-center justify-between w-full">
                    <p className="text-sm font-medium">Country Filter</p>
                    {localCountryFilters.length > 0 && (
                      <Button
                        size="sm"
                        variant={showSelectedCountriesOnly ? 'solid' : 'flat'}
                        color={showSelectedCountriesOnly ? 'primary' : 'default'}
                        onPress={handleToggleShowSelectedCountries}
                      >
                        {showSelectedCountriesOnly ? 'Show all' : 'Show selected'}
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-default-500">
                    Selected countries will show by default
                  </p>
                  <p className="text-sm text-default-300">
                    None selected = all countries shown
                  </p>
                </CardHeader>
                <CardBody className="gap-3">
                  {/* Search Input */}
                  <Input
                    placeholder="Search countries..."
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                    isClearable
                    onClear={() => setSearchQuery('')}
                    size="sm"
                    classNames={{ input: 'text-base' }}
                  />

                  {/* Select All / Clear All buttons */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={handleSelectAll}
                      isDisabled={filteredCountryOptionsDisplay.length === 0}
                    >
                      Select All
                    </Button>
                    <Button
                      size="sm"
                      variant="flat"
                      onPress={handleClearAll}
                      isDisabled={visibleSelectedCount === 0}
                    >
                      Clear All
                    </Button>
                    <span className="text-xs text-default-400 ml-auto">
                      {localCountryFilters.length === 0 ||
                      localCountryFilters.length === COUNTRY_OPTIONS.length
                        ? 'All countries'
                        : `${localCountryFilters.length} selected (${filteredCountryOptionsDisplay.length} shown)`}
                    </span>
                  </div>

                  {/* Country List */}
                  <ScrollShadow size={5} className="min-h-16" hideScrollBar>
                    {filteredCountryOptionsDisplay.length > 0 ? (
                      <CheckboxGroup
                        value={localCountryFilters}
                        onValueChange={handleCountryFiltersChange}
                      >
                        {filteredCountryOptionsDisplay.map((option) => (
                          <Checkbox key={option.value} value={option.value}>
                            {option.label}
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    ) : (
                      <p className="text-sm text-default-400 py-4 text-center">
                        {showSelectedCountriesOnly
                          ? 'No countries selected'
                          : 'No countries match your search'}
                      </p>
                    )}
                  </ScrollShadow>
                </CardBody>
              </Card>

              {/* Advanced Settings link */}
              <Card shadow="sm" className="shrink-0">
                <CardBody>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium">Advanced Settings</p>
                      <p className="text-sm text-default-500">
                        API settings and additional options
                      </p>
                    </div>
                    <Button
                      variant="flat"
                      color="primary"
                      endContent={<ExternalLink className="w-4 h-4" />}
                      onPress={handleNavigateToSettings}
                      aria-label="More Settings"
                      isIconOnly={true}
                    >
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="light"
            onPress={isDirty ? handleDiscard : onClose}
            isDisabled={isSaving}
          >
            {isDirty ? 'Discard' : 'Cancel'}
          </Button>
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={isSaveDisabled}
            isLoading={isSaving}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EconomicCalendarConfigModal;
